import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import dayjs, { ManipulateType } from 'dayjs';
import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import CardContainer from 'src/components/layouts/CardContainer/CardContainer';
import NoDataPlaceholder from 'src/components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import StatisticRow from 'src/components/cardLayouts/StatisticRow/StatisticRow';
import { Plugin } from 'chart.js';
import BarChart from 'src/components/charts/BarChart/BarChart';
import AreaChart from 'src/components/charts/AreaChart/AreaChart';
import Select, { Option } from 'src/components/ui/Select/Select';
import { convertKpiToHeaderItemProps } from 'src/components/layouts/KpiRow.tsx/KpiRow';
import { StockHistoryMode, useStatisticsKpiQuery, useStockHistoryQuery } from 'src/services/kpi';
import { useAllInventoryProducts } from 'src/services/inventory';
import { useRouteAccess } from 'src/hooks/useRouteAccess';
import PeriodBar, { PeriodRange, formatRange, resolvePreset } from 'src/components/ui/PeriodBar/PeriodBar';
import { formatAmount } from 'src/utils/general';
import { useProfile } from 'src/utils/ProfileService';
import { CHART_SERIES, CHART_TICK } from 'src/styles/chartTheme';

const GREY_400 = 'var(--text-disabled)';
const GREY_100 = 'var(--border-subtle)';
const BLUE = CHART_SERIES.light[1];
const PURPLE = CHART_SERIES.light[0];
const CARRIER_ROUTE = 'carrier-analytics/dashboard';
const TOP_N_VALUES = [5, 8, 12, 9999];
const TOP_N_ALL = 9999;

interface Tile {
  key?: string;
  title: string;
  value: number;
  progress: string;
  progress_color: string;
}
interface Series {
  key?: string;
  label: string;
  data: number[];
  color: string;
}
interface CountryItem {
  country?: string;
  percentage?: number;
  total_destination_countries?: number;
}
interface AffinityItem {
  product_a: string;
  product_b: string;
  orders: number;
  share: number;
}
type StatKpi =
  | { kpi_type: 'top_kpis'; data: Tile[] }
  | {
      kpi_type: 'statistics_monthly';
      title: string;
      bucket: 'day' | 'week' | 'month';
      y_labels: string[];
      bucket_dates: string[];
      series: Series[];
    }
  | { kpi_type: 'statistics_countries'; title: string; value: CountryItem[] }
  | {
      kpi_type: 'statistics_returns';
      title: string;
      rate: number;
      prev_rate: number;
      prev_orders: number;
      reasons: { label: string; share: number }[];
      sparkline: number[];
    }
  | { kpi_type: 'statistics_weight'; title: string; y_labels: string[]; series: Series[] }
  | { kpi_type: 'statistics_affinity'; title: string; value: AffinityItem[] };

function useNumberFormat() {
  const { profile, loading } = useProfile();
  const lang = loading ? null : profile.lang;
  return React.useCallback(
    (value: number, decimals = 1) => {
      const factor = 10 ** decimals;
      return formatAmount(Math.round((value ?? 0) * factor) / factor, lang);
    },
    [lang],
  );
}

function roundTileProgress(tiles: Tile[]): Tile[] {
  return tiles.map(tile => {
    const change = parseFloat((tile.progress || '').replace('%', ''));
    return { ...tile, progress: Number.isNaN(change) ? tile.progress : `${Math.round(change)}%` };
  });
}

function Segmented({ options, value, onChange }: { options: string[]; value: number; onChange: (i: number) => void }) {
  return (
    <div className="flex gap-1 rounded-base p-0.5 surface-elevated-darken">
      {options.map((opt, i) => (
        <button
          type="button"
          key={opt}
          onClick={() => onChange(i)}
          className={classNames(
            'rounded-base',
            i === value ? 'typo-text-12--bold surface-elevated text-full' : 'typo-text-12 text-faded',
          )}
          style={{
            padding: '4px 10px',
            border: 'none',
            cursor: 'pointer',
            boxShadow: 'none',
          }}>
          {opt}
        </button>
      ))}
    </div>
  );
}

function MonthlyChart({
  title,
  labels,
  series,
  bucket,
  bucketDates,
  rangeLabel,
}: {
  title: string;
  labels: string[];
  series: Series[];
  bucket: 'day' | 'week' | 'month';
  bucketDates: string[];
  rangeLabel: string;
}) {
  const fmt = useNumberFormat();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sel, setSel] = useState(0);
  const active = series[sel] || { label: '', data: [], color: BLUE };
  const activeLabel = t(`statistics.series.${active.key}`, { defaultValue: active.label });
  const unit = t(`statistics.unit.${bucket}`);
  const nonZero = active.data.filter(v => v > 0);
  const avg = nonZero.length ? Math.round(nonZero.reduce((a, b) => a + b, 0) / nonZero.length) : 0;
  const peakIdx = active.data.indexOf(Math.max(...active.data, 0));
  const step = Math.max(1, Math.ceil(Math.max(1, ...active.data) / 4));

  const handleBarClick = (_label: string, timestamp: string) => {
    const idx = labels.indexOf(timestamp);
    const startIso = bucketDates[idx];
    if (idx < 0 || !startIso) return;
    const width: ManipulateType = bucket;
    const start = dayjs(startIso);
    const end = start.add(1, width);
    const domain = JSON.stringify([
      ['fulfillment_state', 'in', ['shipped', 'delivered']],
      ['picking_ids.date_done', '>=', start.format('YYYY-MM-DD')],
      ['picking_ids.date_done', '<', end.format('YYYY-MM-DD')],
    ]);
    navigate('/outbound', { state: { preselectedDomain: domain } });
  };

  return (
    <CardContainer
      label={title}
      className="h-full"
      actionChildren={
        <Segmented
          options={series.map(s => t(`statistics.series.${s.key}`, { defaultValue: s.label }))}
          value={sel}
          onChange={setSel}
        />
      }>
      <div className="typo-text-12 text-faded" style={{ marginTop: -8, marginBottom: 12 }}>
        {rangeLabel}
      </div>
      <div style={{ height: 240 }}>
        <BarChart
          data={{
            labels,
            datasets: [
              {
                label: activeLabel,
                data: active.data,
                backgroundColor: active.color,
                borderColor: active.color,
                maxBarThickness: 48,
                borderRadius: 4,
              },
            ],
          }}
          onBarClick={handleBarClick}
          options={{
            maintainAspectRatio: false,
            scales: {
              x: { ticks: { autoSkip: false, minRotation: 0, maxRotation: 0 } },
              y: { min: 0, max: step * 4, ticks: { stepSize: step } },
            },
          }}
        />
      </div>
      {avg > 0 && (
        <div className="typo-text-12 text-faded pt-3 mt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {t('statistics.avg_per_bucket', { avg: fmt(avg, 0), label: activeLabel, unit })} &nbsp;·&nbsp;{' '}
          {t('statistics.peak')}: {labels[peakIdx]} ({fmt(active.data[peakIdx] || 0, 0)})
        </div>
      )}
    </CardContainer>
  );
}

function CountriesCard({ title, items }: { title: string; items: CountryItem[] }) {
  const fmt = useNumberFormat();
  const { t } = useTranslation();
  const countries = items.filter(c => c.country);
  const total = items.find(c => c.total_destination_countries != null)?.total_destination_countries;
  return (
    <CardContainer label={title} className="h-full">
      <div className="flex flex-col gap-3">
        {countries.length === 0 && <span className="typo-text-14 text-faded">{t('statistics.no_data')}</span>}
        {countries.map(c => (
          <div key={c.country} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="typo-text-14">{c.country}</span>
              <span className="typo-text-14--medium">{fmt(c.percentage ?? 0, 0)} %</span>
            </div>
            <div style={{ height: 6, background: 'var(--surface-elevated-more)', borderRadius: 999 }}>
              <div style={{ height: 6, width: `${c.percentage}%`, background: BLUE, borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
      {total != null && (
        <div className="typo-text-12 text-faded pt-3 mt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {t('statistics.total_destination_countries', { count: total })}
        </div>
      )}
    </CardContainer>
  );
}

function ReturnsCard({
  rate,
  prevRate,
  prevOrders,
  reasons,
  sparkline,
}: {
  rate: number;
  prevRate: number;
  prevOrders: number;
  reasons: { label: string; share: number }[];
  sparkline: number[];
}) {
  const fmt = useNumberFormat();
  const { t } = useTranslation();
  const spark = sparkline || [];
  const rs = reasons || [];
  const diff = Math.round(rate) - Math.round(prevRate);
  const good = diff <= 0;
  const color = good ? 'var(--status-success-full)' : 'var(--status-error-full)';
  const max = Math.max(1, ...spark);
  const denom = spark.length > 1 ? spark.length - 1 : 1;
  const points = spark.map((v, i) => `${((i / denom) * 220).toFixed(1)},${(40 - (v / max) * 34).toFixed(1)}`).join(' ');
  return (
    <CardContainer label={t('statistics.returns_rate_title')} className="h-full">
      <div className="flex items-baseline gap-3 -mt-8">
        <span className="typo-title-24">{fmt(rate, 0)} %</span>
        {prevOrders > 0 ? (
          <span
            className="typo-text-12--bold inline-flex rounded-full px-2 py-0.5"
            style={{ color, background: `${color}1f` }}>
            {good ? '↓' : '↑'} {fmt(Math.abs(diff), 0)}
            {t('statistics.percentage_points')}
          </span>
        ) : (
          <span className="typo-text-12 text-faded">{t('statistics.no_comparison_data')}</span>
        )}
      </div>
      {spark.length > 1 && (
        <svg width="100%" height="44" viewBox="0 0 220 44" preserveAspectRatio="none" className="mt-8 mb-16 block">
          <polyline points={points} fill="none" stroke={PURPLE} strokeWidth="2" />
        </svg>
      )}
      {rs.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          <span className="typo-text-12 text-faded">{t('statistics.top_return_reasons')}</span>
          {rs.map(r => (
            <div key={r.label} className="flex items-center gap-3">
              <span className="typo-text-14 flex-1">{r.label}</span>
              <div style={{ width: 70, height: 5, background: 'var(--surface-elevated-more)', borderRadius: 999 }}>
                <div style={{ height: 5, width: `${r.share}%`, background: PURPLE, borderRadius: 999 }} />
              </div>
              <span className="typo-text-12 text-faded" style={{ width: 40, textAlign: 'right' }}>
                {fmt(r.share, 0)} %
              </span>
            </div>
          ))}
        </div>
      )}
      {rs.length === 0 && (
        <span className="typo-text-12 text-faded mt-2 block">{t('statistics.returns_share_hint')}</span>
      )}
    </CardContainer>
  );
}

function sharePlugin(total: number): Plugin<'bar'> {
  return {
    id: 'bucketShare',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((bar, i) => {
        const value = Number(chart.data.datasets[0].data[i] ?? 0);
        if (!value) {
          return;
        }
        ctx.save();
        ctx.fillStyle = CHART_TICK;
        ctx.font = '500 12px Manrope, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round((value / total) * 100)} %`, bar.x, bar.y - 6);
        ctx.restore();
      });
    },
  };
}

function WeightCard({
  title,
  labels,
  series,
  avgWeight,
}: {
  title: string;
  labels: string[];
  series: Series[];
  avgWeight?: number;
}) {
  const fmt = useNumberFormat();
  const { t } = useTranslation();
  const counts = series[0]?.data || [];
  const total = counts.reduce((a, b) => a + b, 0) || 1;
  const step = Math.max(1, Math.ceil(Math.max(1, ...counts) / 4));
  return (
    <CardContainer label={title} className="h-full">
      <div className="flex items-baseline gap-3 mb-4 -mt-8">
        <span className="typo-title-24">{fmt(avgWeight || 0)} kg</span>
        <span className="typo-text-12 text-faded">{t('statistics.avg_shipping_weight')}</span>
      </div>
      <div style={{ height: 200 }}>
        <BarChart
          data={{
            labels,
            datasets: [
              {
                label: t('statistics.packages'),
                data: counts,
                backgroundColor: PURPLE,
                borderColor: PURPLE,
                maxBarThickness: 48,
                borderRadius: 4,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            layout: { padding: { top: 18 } },
            scales: {
              x: { ticks: { autoSkip: false, maxRotation: 0 } },
              y: { min: 0, max: step * 4, ticks: { stepSize: step } },
            },
          }}
          plugins={[sharePlugin(total)]}
        />
      </div>
    </CardContainer>
  );
}

function AffinityCard({ title, pairs }: { title: string; pairs: AffinityItem[] }) {
  const fmt = useNumberFormat();
  const { t } = useTranslation();
  return (
    <CardContainer label={title} className="h-full" style={{ minHeight: 260 }}>
      {pairs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <NoDataPlaceholder size="sm" headline={t('statistics.no_affinity_products')} copy="" />
        </div>
      ) : (
        <div className="flex flex-col">
          <div
            className="flex items-center typo-text-12 text-faded pb-5"
            style={{ borderBottom: `1px solid ${GREY_100}` }}>
            <span className="flex-1">{t('statistics.product_combination')}</span>
            <span style={{ width: 140 }} className="text-center">
              {t('statistics.common_orders')}
            </span>
            <span style={{ width: 90 }} className="text-center">
              {t('statistics.share')}
            </span>
          </div>
          {pairs.map(pair => (
            <div
              key={`${pair.product_a}-${pair.product_b}`}
              className="flex items-center py-5"
              style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <div className="flex-1 flex items-center gap-2 typo-text-14">
                <span className="rounded-base px-2 py-1 surface-primary-tint text-primary typo-text-14--medium">
                  {pair.product_a}
                </span>
                <span style={{ color: GREY_400 }}>+</span>
                <span className="rounded-base px-2 py-1 surface-primary-tint text-primary typo-text-14--medium">
                  {pair.product_b}
                </span>
              </div>
              <span style={{ width: 140 }} className="text-center typo-text-14">
                {pair.orders}
              </span>
              <span style={{ width: 90 }} className="text-center typo-text-14 text-faded">
                {fmt(pair.share, 0)} %
              </span>
            </div>
          ))}
        </div>
      )}
    </CardContainer>
  );
}

function CarrierLinkCard({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <CardContainer label={t('statistics.carrier_split')} className="h-full">
      <div className="flex flex-col gap-3">
        <span className="typo-text-14 text-faded">{t('statistics.carrier_hint')}</span>
        <button
          type="button"
          onClick={onOpen}
          className="typo-text-14--medium text-left"
          style={{ color: 'var(--primary)' }}>
          {t('statistics.to_carrier_analytics')} →
        </button>
      </div>
    </CardContainer>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.replace(/./g, c => c + c) : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ModeToggle({
  value,
  onChange,
  lotsDisabled,
  options,
}: {
  value: StockHistoryMode;
  onChange: (mode: StockHistoryMode) => void;
  lotsDisabled: boolean;
  options: { total: string; lots: string };
}) {
  const items: { mode: StockHistoryMode; label: string; disabled: boolean }[] = [
    { mode: 'total', label: options.total, disabled: false },
    { mode: 'lots', label: options.lots, disabled: lotsDisabled },
  ];
  return (
    <div className="flex gap-1 rounded-base p-0.5 surface-elevated-darken">
      {items.map(item => {
        const active = item.mode === value;
        return (
          <button
            type="button"
            key={item.mode}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.mode)}
            className={classNames(
              'rounded-base',
              active ? 'typo-text-12--bold surface-elevated text-full' : 'typo-text-12 text-faded',
            )}
            style={{
              padding: '4px 10px',
              border: 'none',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              boxShadow: 'none',
              opacity: item.disabled ? 0.6 : 1,
            }}>
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function StockHistoryCard({ rangeFrom, rangeTo }: { rangeFrom?: string; rangeTo?: string }) {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const [productId, setProductId] = useState<number | null>(null);
  const [mode, setMode] = useState<StockHistoryMode>('total');
  const [topNIdx, setTopNIdx] = useState(TOP_N_VALUES.indexOf(8));
  const topN = TOP_N_VALUES[topNIdx];

  const { data: products } = useAllInventoryProducts();
  const productOptions: Option[] = useMemo(
    () =>
      (products ?? [])
        .map(item => ({ value: String(item.product_id), label: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [products],
  );

  const didInitDefault = useRef(false);
  useEffect(() => {
    if (didInitDefault.current || !products?.length) return;
    didInitDefault.current = true;
    const withMostStock = [...products].sort((a, b) => (b.on_stock ?? 0) - (a.on_stock ?? 0))[0];
    setProductId(withMostStock.product_id);
  }, [products]);

  const selectedOption = productOptions.find(o => o.value === String(productId));

  const { data, isFetching } = useStockHistoryQuery(productId, mode, rangeFrom, rangeTo, topN);
  const hasLots = data?.has_lots ?? false;
  const series = data?.series ?? [];
  const currentStock = series.reduce((sum, s) => sum + (s.data[s.data.length - 1] ?? 0), 0);

  return (
    <CardContainer
      label={t('statistics.stock_history_title')}
      className="h-full"
      actionChildren={
        <div className="flex items-center gap-5">
          <div style={{ minWidth: 240 }}>
            <Select
              options={productOptions}
              defaultValue={selectedOption}
              isSearchable
              placeholder={t('statistics.select_product')}
              onChange={(option: Option) => setProductId(option ? Number(option.value) : null)}
            />
          </div>
          <ModeToggle
            value={mode}
            onChange={setMode}
            lotsDisabled={!hasLots}
            options={{ total: t('statistics.stock_history_total'), lots: t('statistics.stock_history_lots') }}
          />
        </div>
      }>
      {productId == null ? (
        <div className="flex items-center justify-center typo-text-14 text-faded" style={{ height: 260 }}>
          {t('statistics.stock_history_select_hint')}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-3" style={{ marginTop: -8, marginBottom: 12 }}>
            <span className="typo-title-24">{fmt(currentStock, 0)}</span>
            <span className="typo-text-12 text-faded">{t('statistics.stock_history_current')}</span>
          </div>
          <div style={{ height: 280 }}>
            <AreaChart
              data={{
                labels: data?.y_labels ?? [],
                datasets: series.map(s => ({
                  label: s.label,
                  data: s.data,
                  borderColor: s.color,
                  backgroundColor: hexToRgba(s.color, 0.12),
                  fill: true,
                })),
              }}
              options={{
                plugins: { legend: { display: false } },
                scales: { x: { stacked: false }, y: { stacked: false } },
              }}
            />
          </div>
          <div className="grid items-center gap-4 pt-3" style={{ gridTemplateColumns: '1fr auto 1fr', minHeight: 24 }}>
            <span className="typo-text-12 text-faded">{isFetching ? t('general.loading') : ''}</span>
            <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1">
              {mode === 'lots' &&
                series.map(s => (
                  <span key={s.label} className="flex items-center gap-1.5 typo-text-12 text-faded">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: `2px solid ${s.color}`,
                        background: '#fff',
                        display: 'inline-block',
                      }}
                    />
                    {s.label}
                  </span>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              {mode === 'lots' && (
                <>
                  <span className="typo-text-12 text-faded">{t('statistics.stock_history_max_lots')}</span>
                  <Segmented
                    options={TOP_N_VALUES.map(v => (v === TOP_N_ALL ? t('statistics.stock_history_all') : String(v)))}
                    value={topNIdx}
                    onChange={setTopNIdx}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </CardContainer>
  );
}

function Statistics() {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const navigate = useNavigate();
  const { hasAccess } = useRouteAccess();
  const showCarrierLink = hasAccess(CARRIER_ROUTE);
  const [range, setRange] = useState<PeriodRange>(() => resolvePreset('last_30_days'));
  const { data } = useStatisticsKpiQuery(range.from, range.to);
  const rangeLabel = formatRange(range);
  const kpis = data as StatKpi[] | undefined;

  const top = kpis?.find(k => k.kpi_type === 'top_kpis') as Extract<StatKpi, { kpi_type: 'top_kpis' }> | undefined;
  const monthly = kpis?.find(k => k.kpi_type === 'statistics_monthly') as
    | Extract<StatKpi, { kpi_type: 'statistics_monthly' }>
    | undefined;
  const countries = kpis?.find(k => k.kpi_type === 'statistics_countries') as
    | Extract<StatKpi, { kpi_type: 'statistics_countries' }>
    | undefined;
  const returns = kpis?.find(k => k.kpi_type === 'statistics_returns') as
    | Extract<StatKpi, { kpi_type: 'statistics_returns' }>
    | undefined;
  const weight = kpis?.find(k => k.kpi_type === 'statistics_weight') as
    | Extract<StatKpi, { kpi_type: 'statistics_weight' }>
    | undefined;
  const affinity = kpis?.find(k => k.kpi_type === 'statistics_affinity') as
    | Extract<StatKpi, { kpi_type: 'statistics_affinity' }>
    | undefined;

  const weightTile = top?.data.find(d => d.key === 'avg_weight_per_package');

  return (
    <Container>
      <HeadlineWithButtons headline={t('navbar.statistics')} />
      <div className="flex flex-col gap-20 mb-20">
        <PeriodBar value={range} onChange={setRange} />
        {top && (
          <StatisticRow
            items={convertKpiToHeaderItemProps(roundTileProgress(top.data), t).map((item, i) => ({
              ...item,
              title: t(`statistics.tiles.${top.data[i].key}`, { defaultValue: item.title }),
              description: t(`period.labels.${range.key}`, { defaultValue: rangeLabel }),
              kpiNumber: fmt(
                top.data[i].value,
                ['shipped_orders', 'returns_rate'].includes(top.data[i].key ?? '') ? 0 : 1,
              ),
            }))}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          {monthly && (
            <div className="lg:col-span-2">
              <MonthlyChart
                title={t(`statistics.monthly_title.${monthly.bucket}`, { defaultValue: monthly.title })}
                labels={monthly.y_labels}
                series={monthly.series}
                bucket={monthly.bucket}
                bucketDates={monthly.bucket_dates}
                rangeLabel={rangeLabel}
              />
            </div>
          )}
          {countries && (
            <div className="lg:col-span-1">
              <CountriesCard title={t('statistics.countries_title')} items={countries.value} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1">
          <StockHistoryCard rangeFrom={range.from} rangeTo={range.to} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          {returns && (
            <div className="lg:col-span-1">
              <ReturnsCard
                rate={returns.rate}
                prevRate={returns.prev_rate}
                prevOrders={returns.prev_orders}
                reasons={returns.reasons}
                sparkline={returns.sparkline}
              />
            </div>
          )}
          {weight && (
            <div className="lg:col-span-2">
              <WeightCard
                title={t('statistics.weight_title')}
                labels={weight.y_labels}
                series={weight.series}
                avgWeight={weightTile?.value}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          {affinity && (
            <div className={showCarrierLink ? 'lg:col-span-2' : 'lg:col-span-3'}>
              <AffinityCard title={t('statistics.affinity_title')} pairs={affinity.value} />
            </div>
          )}
          {showCarrierLink && (
            <div className="lg:col-span-1">
              <CarrierLinkCard onOpen={() => navigate(`/${CARRIER_ROUTE}`)} />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

export default Statistics;
